import { codeWords, daysUntil, shortDate, type Vendor } from '../../lib/api'
import { chipWord } from '../../lib/status'
import { dataHandled } from './facts'
import { BandChip } from '../BandChip'
import { Status, Toggletip } from '../kit'
import { Chip } from '../StatusChip'
import { daysWord, findingsReading, severityTone } from './facts'
import { reasonWords } from '../../lib/bandLine'
import '../../styles/band-line.css'

// Four facts under the vendor's name, in the order a reader asks them: how
// risky, what is still owed, what data it holds, when it is next looked at.
// Each is a small label with its value beside it, and a fact the record does
// not hold says so in two words rather than leaving a gap.
//
// They were a strip of their own under the header, 88 px on every section;
// they are one line under the header now, the plan's fact line (release 3.3,
// the 7 Oct plan section 3.3), with the data beside them.
//
// # WHAT SITS WHERE
//
// A calm note ("none overdue", "in 179 days") is part of the label line, in
// the label's own ink. Something past its date is a chip beside the value, with
// the clock the late chip carries everywhere else: the next review used to say
// so in red letters alone.
//
// Where the questionnaire is was a fifth fact here, and the same reading was on
// the Overview's Questionnaire panel, in the Risk part and behind the header's
// button (release 3.2 removal item 2). It is read in one place now: the
// Questionnaire panel on the Overview.

// Late, as every chip draws it: amber with a clock, not the red kept for the
// critical.
const OVERDUE = chipWord('finding', 'OPEN', true)

export function Highlights({ vendor }: { vendor: Vendor }) {
  const findings = findingsReading(vendor)
  const data = dataHandled(vendor)
  const due = daysUntil(vendor.nextDueOn)
  const band = vendor.currentBand
  const beforeActivation = vendor.status === 'DRAFT' || vendor.status === 'PENDING_APPROVAL'
  const owed = findings.standing === 0 ? '' : findings.overdue === 0 ? 'none overdue' : ''
  const why = reasonWords(vendor.bandReason)

  return (
    <dl className="vd-highlights" aria-label="Highlights">
      <div className="vd-hl">
        <dt>Risk band</dt>
        <dd className="vd-hl-v">
          <BandChip band={band} />
          {band === 'INSUFFICIENT_EVIDENCE' && (
            <Toggletip label="About insufficient evidence">
              A statement about evidence, not about the vendor. Coverage fell below the model floor, so the band was withheld.
            </Toggletip>
          )}
        </dd>
        {(band === 'HIGH' || band === 'CRITICAL') && beforeActivation && <dd className="vd-hl-d">acceptance needed to activate</dd>}
        {/* Why the band, in one line (release 3.3, N2): the step that decided
            it, in its own words. One line here, the whole of it to a screen
            reader and on the Overview's Risk card, and marked in "How the
            band was reached". */}
        {why && (
          <dd className="vd-hl-d band-why-one" title={why}>
            {why}
          </dd>
        )}
      </div>

      <div className="vd-hl">
        <dt>Open findings</dt>
        <dd className="vd-hl-v">
          <span className="vd-num">{findings.standing}</span>
          {findings.worst && <Status tone={severityTone(findings.worst)}>{codeWords(findings.worst)}</Status>}
          {findings.overdue != null && findings.overdue > 0 && <Chip s={OVERDUE}>{findings.overdue} overdue</Chip>}
        </dd>
        {owed && <dd className="vd-hl-d">{owed}</dd>}
      </div>

      <div className="vd-hl">
        <dt>Data</dt>
        <dd className="vd-hl-v" title={data.full}>
          {data.words}
        </dd>
        {data.line && <dd className="vd-hl-d">{data.line}</dd>}
      </div>

      <div className="vd-hl">
        <dt>Next review</dt>
        <dd className="vd-hl-v">
          {vendor.nextDueOn ? <span className="kit-mono vd-date">{shortDate(vendor.nextDueOn)}</span> : 'Not set'}
          {due != null && due < 0 && <Chip s={OVERDUE}>{daysWord(due)} overdue</Chip>}
        </dd>
        {due != null && due >= 0 && <dd className="vd-hl-d">{due === 0 ? 'today' : 'in ' + daysWord(due)}</dd>}
      </div>
    </dl>
  )
}
